import React, {useEffect, useMemo, useState} from 'react';

import {PageContainer} from 'components';
import {
    useLazyGetCandidatesQuery,
    useLazyGetSessionsQuery,
    useLazyGetPrssQuery,
    useLazyGetAvailableServiceTypesQuery,
} from 'store/post-results-services/postResultsServicesApi';
import {PrsServiceTypeOptions, PrsStatusOptions} from "core/const";
import { Option } from 'react-multi-select-component';
import {generatePath, useNavigate} from "react-router-dom";
import {ROUTES} from "core";
import {MultiSelect} from "components/extro/MultiSelect";

import { Select } from '@britishcouncil/react-extro-ui';
import {
    LoadingCard, PageLoader
} from '@britishcouncil/react-solas-ors3';
import {Button} from '@britishcouncil/react-solas';

import {ScopedExtroProvider} from "../../components/extro/ScopedExtroProvider";

import * as S from './PostResultsServices.style';
import {RequestsList} from './views/RequestsList/RequestsList';

export const PostResultsServices = () => {
    const [
        getSessionsQuery,
        {data: sessions, isLoading: sessionIsLoading}
    ] = useLazyGetSessionsQuery();

    const [
        getPrssQuery,
        {data: prss, isSuccess: prsIsSuccess, isLoading: prsIsLoading}
    ] = useLazyGetPrssQuery();

    const navigate = useNavigate();
    const [getCandidatesQuery, {data: candidates, isLoading: candidateIsLoading}] = useLazyGetCandidatesQuery();

    const [chooseModeEnabled, setChooseModeEnabled] = useState(false);
    const [getAvailableServiceTypesQuery, getAvailableServiceTypesQueryData] = useLazyGetAvailableServiceTypesQuery();

    const [filterValue, setFilterValue] = useState<{
        session: { value: string, label:string } | undefined;
        candidate: { value: string, label:string } | undefined;
        serviceType: Option[];
        status: Option[];
    }>({session: undefined, candidate: undefined, serviceType: [], status: []});

    useEffect(() => {
        getSessionsQuery();
    }, [getSessionsQuery]);

    useEffect(() => {
        getAvailableServiceTypesQuery()
    }, [getAvailableServiceTypesQuery]);

    useEffect(() => {
        if (filterValue.session) {
            getCandidatesQuery(filterValue.session.value)
        }
    }, [filterValue.session, getCandidatesQuery]);

    useEffect(() => {
        if (filterValue.session && filterValue.candidate) {
            getPrssQuery({
                candidateId: filterValue.candidate.value,
                sessionId: filterValue.session.value
            })
        }
    }, [filterValue.session, filterValue.candidate, getPrssQuery]);
    
    useEffect(() => {
        if (prss) {
            const availableStatuses = prss.map(e => e.status);
            setFilterValue(prevFilters => ({
                ...prevFilters,
                status: prevFilters.status.filter(option => availableStatuses.includes(option.value))
            }));
        }
    }, [prss]);

    const prsStatusOptions = useMemo(
        () => {
            if (prss) {
                const usedStatuses = prss.map(e => e.status);
                return PrsStatusOptions
                    .filter(o => usedStatuses.some(t => t === o.name))
                    .map(o => ({value: o.id, label: o.name}));
            } else {
                return [];
            }
        },
        [prss]
    );

    const serviceTypesOptions = useMemo(
        () => {
            if (prss) {
                const usedTypes = prss.map(e => e.serviceTypeId);
                return PrsServiceTypeOptions
                    .filter(o => usedTypes.some(t => t === o.id))
                    .map(o => ({value: o.id, label: o.name}));
            } else {
                return [];
            }
        },
        [prss]
    );
    
    const sessionsOptions = useMemo(
        () => {
            if (sessions) {
                return sessions
                    .map(o => ({value: o.id, label: o.name}));
            } else {
                return [];
            }
        },
        [sessions]
    )
    
    const candidateOptions = useMemo(
        () => {
            if (candidates) {
                return candidates
                    .map(o => ({value: o.id, label: o.name}));
            } else {
                return [];
            }
        },
        [candidates]
    )

    const filteredPrss = useMemo(
        () => {
            if (prss) {
                return prss.filter(p =>
                    (filterValue.serviceType.length === 0 || filterValue.serviceType.some(option => p.serviceTypeId === option.value)) &&
                    (filterValue.status.length === 0 || filterValue.status.some(option => p.status === option.value))
                );
            }
            return []
        }, [prss, filterValue]
    );
    if (sessionIsLoading || getAvailableServiceTypesQueryData.isLoading) {
        return <LoadingCard/>
    }

    return (
        <ScopedExtroProvider>
            <S.StyledPageContainer>
                <PageContainer>
                    <div>
                        <S.Header>
                            <span>Post results services</span>
                            {getAvailableServiceTypesQueryData.data &&
                                <Button disabled={chooseModeEnabled || getAvailableServiceTypesQueryData.data.length === 0}
                                        size="xs"
                                        intent="primary"
                                        onClick={() => {
                                            navigate(generatePath(ROUTES.CREATE_POST_RESULTS_SERVICE));
                                        }}
                                >
                                    Create request
                                </Button>
                            }
                        </S.Header>
                    </div>

                    <S.ContentWrapper>

                        {!sessionIsLoading && sessions && sessions.length === 0 &&
                            <S.EmptyStateInformation>Nothing to display</S.EmptyStateInformation>}

                        {sessions && sessions.length > 0 && (
                            <>
                                <S.FiltersRow>
                                    <div className="form-group">
                                        <label>Session</label>
                                        <Select
                                            value={filterValue.session}
                                            isDisabled={chooseModeEnabled}
                                            isMulti={false}
                                            options={sessionsOptions}
                                            onChange={(option) => {
                                                setFilterValue(() => ({
                                                    session: option != null ? option : undefined,
                                                    candidate: undefined,
                                                    status: [],
                                                    serviceType: []
                                                }))
                                            }}/>
                                    </div>

                                    <div className="form-group">
                                        <label>Candidate</label>
                                        <Select
                                            value={filterValue.candidate}
                                            isDisabled={chooseModeEnabled}
                                            isMulti={false}
                                            options={candidateOptions}
                                            onChange={(option) => {
                                                setFilterValue(currentFilters => ({
                                                    ...currentFilters,
                                                    candidate: option != null ? option : undefined,
                                                    status: [],
                                                    serviceType: []
                                                }))
                                            }}/>
                                    </div>
                                </S.FiltersRow>

                                {candidateIsLoading && <PageLoader/>}

                                {(!filterValue.session || !filterValue.candidate) &&
                                    <S.EmptyResultInformation>Select session & candidate to display
                                        post results services requests</S.EmptyResultInformation>}

                                {filterValue.session && filterValue.candidate && (
                                    <>
                                        <S.FiltersRow>
                                            <div className="form-group">
                                                <label>Post result services type</label>
                                                <MultiSelect
                                                    disableSearch={true}
                                                    hasSelectAll={true}
                                                    maximumDisplayedItems={1}
                                                    placeholder='All'
                                                    labelledBy='All'
                                                    value={filterValue.serviceType}
                                                    options={serviceTypesOptions}
                                                    onChange={(selected: Option[]) => {
                                                        setFilterValue(currentFilters => ({
                                                            ...currentFilters,
                                                            serviceType: selected
                                                        }))
                                                    }
                                                    }
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Status</label>
                                                <MultiSelect
                                                    disableSearch={true}
                                                    hasSelectAll={true}
                                                    disabled={chooseModeEnabled}
                                                    maximumDisplayedItems={1}
                                                    placeholder='All'
                                                    labelledBy='All'
                                                    value={filterValue.status}
                                                    options={prsStatusOptions}
                                                    onChange={(selected: Option[]) => {
                                                        setFilterValue(currentFilters => ({
                                                            ...currentFilters,
                                                            status: selected
                                                        }))
                                                    }
                                                    }
                                                />
                                            </div>
                                        </S.FiltersRow>
                                    </>
                                )}
                            </>
                        )}

                        {prsIsLoading && <LoadingCard/>}

                        {prsIsSuccess && prss && filterValue.session && filterValue.candidate &&

                            <>
                                {!prsIsLoading && filteredPrss.length === 0 &&
                                    <S.EmptyStateInformation>Nothing to
                                        display</S.EmptyStateInformation>
                                }

                                <RequestsList
                                    onChangeHasSelectedMode={setChooseModeEnabled}
                                    candidateId={filterValue.candidate.value}
                                    data={filteredPrss}/>
                            </>
                        }
                    </S.ContentWrapper>
                </PageContainer>
            </S.StyledPageContainer>
        </ScopedExtroProvider>
    );
};
